import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import {
  CHATBOT_GREETING,
  CHATBOT_GREETING_LINKS,
  CHATBOT_QUICK_PROMPTS,
  findChatbotResponse,
  MOSSMATRIX_CONTACT_EMAIL,
} from "../lib/chatbotKnowledge";
import { useShellTheme } from "./layout/ShellTheme";
import { useCart } from "../context/CartContext";

/**
 * MossMatrix helper chat, Phase A: rule + FAQ matcher (`findChatbotResponse`).
 * On dark homepage, panel follows dark forest styling; cream pages keep light UI.
 */
function ChatLink({ link, dark }) {
  const cls = dark
    ? "inline-flex items-center rounded-full border border-white/20 bg-white/[0.06] px-3 py-1 text-[11px] font-medium text-ivory hover:border-gold hover:text-goldhover transition"
    : "inline-flex items-center rounded-full border border-forest/25 bg-cream px-3 py-1 text-[11px] font-medium text-forest hover:border-gold hover:bg-white transition shadow-sm";
  if (link.href) {
    return (
      <a
        href={link.href}
        className={cls}
        target={link.external ? "_blank" : undefined}
        rel={link.external ? "noopener noreferrer" : undefined}
      >
        {link.label}
      </a>
    );
  }
  if (link.path) {
    return (
      <Link to={link.path} className={cls}>
        {link.label}
      </Link>
    );
  }
  return null;
}

export default function MossMatrixChatbot() {
  const dark = useShellTheme() === "dark";
  const { open: cartOpen } = useCart();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);

  // Hide Help while the cart drawer is open so it never covers checkout controls
  useEffect(() => {
    if (cartOpen) setOpen(false);
  }, [cartOpen]);

  const seedGreeting = useCallback(() => {
    setMessages([{ role: "assistant", text: CHATBOT_GREETING, links: CHATBOT_GREETING_LINKS }]);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) seedGreeting();
  }, [open, messages.length, seedGreeting]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const pushAssistant = (text, links) => {
    setMessages((m) => [...m, { role: "assistant", text, links }]);
  };

  const handleSend = (raw) => {
    const text = String(raw || input).trim();
    if (!text) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);

    const { answer, links } = findChatbotResponse(text);
    pushAssistant(answer, links);
  };

  if (cartOpen) return null;

  return (
    <div
      className="fixed z-[200] pointer-events-none left-3 right-3 bottom-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:left-auto sm:right-6 sm:bottom-6 sm:w-[min(420px,calc(100vw-3rem))] flex flex-col items-stretch sm:items-end gap-3"
      aria-live="polite"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.99 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            id="mossmatrix-chat-panel"
            aria-labelledby="chatbot-title"
            className={
              dark
                ? "pointer-events-auto w-full flex flex-col min-h-0 max-h-[min(70vh,540px)] rounded-2xl overflow-hidden border border-white/16 bg-panel shadow-[0_22px_48px_-8px_rgba(0,0,0,0.55)]"
                : "pointer-events-auto w-full flex flex-col min-h-0 max-h-[min(70vh,540px)] rounded-2xl overflow-hidden border-2 border-forest/15 bg-offwhite shadow-[0_22px_48px_-8px_rgba(14,26,20,0.28),0_0_0_1px_rgba(212,197,169,0.35)]"
            }
          >
            <div
              className={
                dark
                  ? "shrink-0 flex items-center justify-between gap-3 px-4 py-3.5 bg-moss text-ivory border-b border-white/12"
                  : "shrink-0 flex items-center justify-between gap-3 px-4 py-3.5 bg-forest text-cream border-b border-forest"
              }
            >
              <div className="min-w-0">
                <div
                  className={`text-[10px] tracking-[0.22em] uppercase font-medium ${
                    dark ? "text-goldhover/90" : "text-cream/75"
                  }`}
                >
                  MossMatrix
                </div>
                <div className="font-sans font-semibold text-lg leading-tight truncate pr-2" id="chatbot-title">
                  Helper
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={
                  dark
                    ? "shrink-0 w-10 h-10 rounded-xl border border-white/20 bg-void grid place-items-center hover:border-gold/50 transition text-ivory"
                    : "shrink-0 w-10 h-10 rounded-xl border border-cream/25 bg-forest grid place-items-center hover:bg-cream/15 hover:border-cream/40 transition text-cream"
                }
                aria-label="Close chat"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>

            <div
              ref={scrollRef}
              className={`flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-3 ${
                dark ? "bg-void" : "bg-[#FAF8F3]"
              }`}
            >
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[min(100%,20rem)] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                      msg.role === "user"
                        ? dark
                          ? "bg-gold text-void rounded-br-md"
                          : "bg-forest text-cream rounded-br-md"
                        : dark
                          ? "bg-moss border border-white/14 text-ivory/90 rounded-bl-md"
                          : "bg-white border border-cream text-forest/90 rounded-bl-md"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                    {msg.links && msg.links.length > 0 ? (
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {msg.links.map((l, j) => (
                          <ChatLink key={`${l.label}-${j}`} link={l} dark={dark} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div
              className={`shrink-0 px-3 pt-2.5 pb-3 ${
                dark ? "border-t border-white/12 bg-moss" : "border-t-2 border-cream bg-cream/90"
              }`}
            >
              <div className="flex flex-wrap gap-2 mb-2.5">
                {CHATBOT_QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleSend(p)}
                    className={
                      dark
                        ? "rounded-full border border-white/16 bg-void px-3 py-1.5 text-[11px] leading-snug text-ivory hover:border-gold/50 transition text-left max-w-full"
                        : "rounded-full border border-forest/20 bg-white px-3 py-1.5 text-[11px] leading-snug text-forest hover:border-gold hover:bg-cream/80 transition text-left max-w-full"
                    }
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 items-end">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  rows={1}
                  placeholder="Ask about sea moss, storage, or UK delivery…"
                  className={
                    dark
                      ? "flex-1 min-w-0 resize-none rounded-2xl border border-white/16 bg-void px-3.5 py-2.5 text-sm text-ivory placeholder:text-sage/70 max-h-24"
                      : "flex-1 min-w-0 resize-none rounded-2xl border-2 border-cream bg-white px-3.5 py-2.5 text-sm text-forest placeholder:text-botanical/50 max-h-24 shadow-inner"
                  }
                  aria-label="Message"
                />
                <button
                  type="button"
                  onClick={() => handleSend()}
                  className="shrink-0 w-12 h-12 rounded-2xl bg-gold text-void grid place-items-center hover:bg-goldhover transition shadow-md border border-goldhover/30"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </div>
              <div
                className={`mt-2.5 flex flex-wrap gap-x-3 gap-y-1 justify-start items-center text-[10px] ${
                  dark ? "text-sage/80" : "text-botanical/70"
                }`}
              >
                <a
                  href={`mailto:${MOSSMATRIX_CONTACT_EMAIL}`}
                  className={`underline underline-offset-2 ${dark ? "hover:text-goldhover" : "hover:text-forest"}`}
                >
                  Email {MOSSMATRIX_CONTACT_EMAIL}
                </a>
                <Link
                  to="/contact"
                  className={`underline underline-offset-2 ${dark ? "hover:text-goldhover" : "hover:text-forest"}`}
                >
                  Contact
                </Link>
                <Link
                  to="/#choose-sea-moss"
                  className={`underline underline-offset-2 ${dark ? "hover:text-goldhover" : "hover:text-forest"}`}
                >
                  Order
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        layout
        onClick={() => {
          setOpen((o) => !o);
          if (!open && messages.length === 0) seedGreeting();
        }}
        className={
          dark
            ? "pointer-events-auto self-end flex items-center gap-2 pl-3 pr-1.5 py-1.5 sm:pl-4 sm:pr-2 sm:py-2 rounded-2xl bg-moss text-ivory border border-gold/45 shadow-[0_12px_28px_-6px_rgba(0,0,0,0.55)] hover:border-gold/70 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            : "pointer-events-auto self-end flex items-center gap-2 pl-3 pr-1.5 py-1.5 sm:pl-4 sm:pr-2 sm:py-2 rounded-2xl bg-forest text-cream border-2 border-gold/45 shadow-[0_12px_28px_-6px_rgba(14,26,20,0.45)] hover:bg-[#1a2e24] hover:border-gold/70 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite"
        }
        aria-expanded={open}
        aria-controls="mossmatrix-chat-panel"
        aria-label={open ? "Close MossMatrix helper chat" : "Open MossMatrix helper chat"}
      >
        <span className="hidden sm:inline font-sans font-medium text-sm tracking-wide pr-0.5 sm:pr-1">Help</span>
        <span
          className={`grid place-items-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl border ${
            dark ? "bg-gold/15 text-ivory border-gold/40" : "bg-gold/20 text-cream border-gold/35"
          }`}
        >
          {open ? (
            <X className="w-5 h-5" strokeWidth={1.75} />
          ) : (
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} />
          )}
        </span>
      </motion.button>
    </div>
  );
}
